/**
 * Episodes as pages: which ones get one, and at what address.
 *
 * Shared by the app and the Worker, as one file rather than two copies: the
 * address of an episode depends on every other episode (two with the same
 * title and town must not collide), so a rule kept in two places would drift
 * the first time either changed, and a link written by the Worker would open
 * nothing in the app. Nothing here touches the DOM, so the Worker can import
 * it as it is.
 *
 * An episode is a programme, a date and a title, exactly as the venue card
 * groups "the others of the same episode". It gets a page from two venues up,
 * and only with a title: with one venue, the page would be the venue's card
 * again, which is every Cucine da Incubo episode, and a search engine keeps
 * one of two identical pages.
 */

/** An episode gets a page from this many venues up. */
export const MIN_EPISODE = 2;

/** As `zoneSlug`: lower case, no accents, words joined by dashes. */
function slugify(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The key that groups a venue with the others of its episode. */
export function episodeKey(programma, data, titolo) {
  return `${programma ?? ''}|${data ?? ''}|${titolo ?? ''}`;
}

export function venueEpisodeKey(venue) {
  return episodeKey(venue.programma, venue.puntata?.data, venue.puntata?.titolo);
}

/**
 * The town an episode is about, when its venues agree on one.
 *
 * More than half of them in the same zone: a Foodish episode in Milan with one
 * venue in San Donato Milanese is still the Milan one. Where they spread over
 * several places — the Costa degli Dei, the Oltrepò — there is no one town to
 * name, and the title already names the area.
 */
function episodeTown(zoneCounts, total) {
  let best = null;
  let most = 0;
  for (const [zona, n] of zoneCounts) {
    if (n > most) {
      best = zona;
      most = n;
    }
  }
  return best && most * 2 > total ? best : null;
}

/**
 * Every episode with a page, from one row per episode and zone:
 * `{ programma, data, titolo, zona, n }`. The Worker gets those rows from one
 * grouped query; the app counts them from the catalogue it already holds.
 *
 * Returns `{ byKey, bySlug }`, both to the same episode objects:
 * `{ key, slug, programma, data, titolo, citta, n }`.
 *
 * Collisions go to the older episode first, which keeps the plain address: an
 * episode added later gets the year, and if even that is taken, the date. An
 * address already handed out never changes when a new episode arrives.
 */
export function episodeIndex(rows) {
  const groups = new Map();
  for (const row of rows) {
    const key = episodeKey(row.programma, row.data, row.titolo);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        programma: row.programma,
        data: row.data ?? null,
        titolo: row.titolo ?? null,
        zones: new Map(),
        n: 0,
      };
      groups.set(key, group);
    }
    const zona = row.zona ?? null;
    if (zona) group.zones.set(zona, (group.zones.get(zona) ?? 0) + row.n);
    group.n += row.n;
  }

  const byKey = new Map();
  const bySlug = new Map();
  const ordered = [...groups.values()]
    .filter((group) => group.n >= MIN_EPISODE && group.titolo)
    .sort((a, b) => (a.data ?? '').localeCompare(b.data ?? '') || a.key.localeCompare(b.key));

  for (const group of ordered) {
    const citta = episodeTown(group.zones, group.n);
    const base = slugify(
      [group.programma, group.titolo, townToAdd(group.titolo, citta)].filter(Boolean).join(' ')
    );
    if (!base) continue;
    const year = (group.data ?? '').slice(0, 4);
    let slug = base;
    if (bySlug.has(slug) && year) slug = `${base}-${year}`;
    if (bySlug.has(slug) && group.data) slug = `${base}-${group.data}`;
    if (bySlug.has(slug)) continue;

    const episode = {
      key: group.key,
      slug,
      programma: group.programma,
      data: group.data,
      titolo: group.titolo,
      citta,
      n: group.n,
    };
    byKey.set(group.key, episode);
    bySlug.set(slug, episode);
  }
  return { byKey, bySlug };
}

/** The same rows `episodeIndex` wants, counted from a list of venues. */
export function episodeRows(venues) {
  const counts = new Map();
  for (const venue of venues) {
    const key = `${venueEpisodeKey(venue)}|${venue.zona ?? ''}`;
    const row = counts.get(key);
    if (row) row.n += 1;
    else {
      counts.set(key, {
        programma: venue.programma,
        data: venue.puntata?.data ?? null,
        titolo: venue.puntata?.titolo ?? null,
        zona: venue.zona ?? null,
        n: 1,
      });
    }
  }
  return [...counts.values()];
}

/** Winner first, then by name: the order an episode's venues are listed in. */
export function winnersFirst(a, b) {
  if (Boolean(a.vincitore) !== Boolean(b.vincitore)) return a.vincitore ? -1 : 1;
  return (a.nome ?? '').localeCompare(b.nome ?? '', 'it');
}

/** "Foodish · Milano · 8 ottobre 2026": the line under an episode's title. */
export function episodeSubtitle(episode, programName, date) {
  return [programName, episode.citta, date].filter(Boolean).join(' · ');
}

/**
 * Other episodes of the same programme in the same town, most recent first:
 * the way on to the next page, for a visitor and for a crawler alike.
 */
export function siblingEpisodes(index, episode, limit = 10) {
  if (!episode.citta) return [];
  return [...index.bySlug.values()]
    .filter(
      (other) =>
        other.slug !== episode.slug &&
        other.programma === episode.programma &&
        other.citta === episode.citta
    )
    .sort((a, b) => (b.data ?? '').localeCompare(a.data ?? ''))
    .slice(0, limit);
}

/** Words of a zone that say nothing about which place it is. */
const GENERIC = new Set([
  'alta', 'alto', 'bassa', 'basso', 'centro', 'costa', 'della', 'delle', 'dello',
  'degli', 'lago', 'provincia', 'storico',
]);

/**
 * The town, where the title does not already say it.
 *
 * Said already when one of the zone's own words stands whole in the title:
 * "del Pigneto" names "Pigneto, Roma", "della riva trentina del Garda" names
 * "Lago di Garda". "Di cucina di lago" does not — "lago" names no lake — and
 * "romane" is not "Roma": the town is added, because people search the town.
 */
function townToAdd(titolo, citta) {
  if (!citta) return null;
  const words = new Set(slugify(titolo).split('-'));
  const said = slugify(citta)
    .split('-')
    .some((word) => word.length >= 4 && !GENERIC.has(word) && words.has(word));
  return said ? null : citta;
}

export function episodeTownToAdd(episode) {
  return townToAdd(episode.titolo, episode.citta);
}

/** What to call an episode with no title of its own. */
export function episodeName(episode, date) {
  return episode.titolo ?? (date ? `Puntata del ${date}` : 'Puntata');
}

/** The most recent episodes with a page, newest first. */
export function latestEpisodes(index, limit = 10) {
  return [...index.bySlug.values()]
    .sort((a, b) => (b.data ?? '').localeCompare(a.data ?? '') || a.key.localeCompare(b.key))
    .slice(0, limit);
}

/**
 * The episodes the list shows above itself, the app and the Worker alike: the
 * latest of each programme on air — one with an episode in the last two
 * weeks — and never more than two. With nothing on air, the latest one.
 *
 * They used to be ten chips in a row that scrolled sideways: on a phone the
 * second was cut in half, and beside the map, where a mouse wheel only scrolls
 * down, the rest could not be reached at all. Three lines were then too many
 * above the list; one would hide a programme the day after another aired.
 */
const ON_AIR_DAYS = 14;
const ON_AIR_MAX = 2;

export function onAirEpisodes(index, now = Date.now()) {
  const latest = latestEpisodes(index, Number.POSITIVE_INFINITY);
  const since = new Date(now - ON_AIR_DAYS * 86400000).toISOString().slice(0, 10);
  const seen = new Set();
  const shown = [];
  for (const episode of latest) {
    if ((episode.data ?? '') < since) break;
    if (seen.has(episode.programma)) continue;
    seen.add(episode.programma);
    shown.push(episode);
    if (shown.length === ON_AIR_MAX) break;
  }
  return shown.length ? shown : latest.slice(0, 1);
}

/** "Miglior riso al salto · Milano", for a line that names the programme apart. */
export function episodeTitleAndTown(episode) {
  return [episode.titolo ?? 'Puntata', episodeTownToAdd(episode)].filter(Boolean).join(' · ');
}

/** "Foodish · Miglior riso al salto · Milano": an episode named in one line. */
export function episodeLabel(episode, programName) {
  return [programName, episode.titolo, episodeTownToAdd(episode)].filter(Boolean).join(' · ');
}

/**
 * The site's pages on Instagram and Facebook, under an episode's venues: each
 * new episode gets a post the day after. The app and the Worker draw the same
 * box (`openEpisode` in `src/main.js`, `episodeBody` in `worker/pagine.js`).
 */
export const FOLLOW = {
  text: 'Seguici su:',
  links: [
    ['Instagram', 'https://www.instagram.com/vistoemangiato.it/'],
    ['Facebook', 'https://www.facebook.com/profile.php?id=61595404912915'],
  ],
};

const MONTHS = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

/** "ottobre 2026", from "2026-10-20"; null without a date. */
function monthOf(data) {
  const match = /^(\d{4})-(\d{2})/.exec(data ?? '');
  return match ? `${MONTHS[Number(match[2]) - 1]} ${match[1]}` : null;
}

/**
 * How many episodes with a page each programme has: the chips of the
 * "Puntate" screen, and the count on a programme's button to it.
 */
export function episodeCounts(index) {
  const counts = new Map();
  for (const episode of index.bySlug.values()) {
    counts.set(episode.programma, (counts.get(episode.programma) ?? 0) + 1);
  }
  return counts;
}

/**
 * Every episode with a page, newest first, under the month it aired: the
 * "Puntate" screen, drawn by the app (`renderEpisodesTab` in `src/main.js`)
 * and written by the Worker at `/puntate` (`episodesBody` in
 * `worker/pagine.js`). `programma` narrows it to one programme.
 *
 * Returns `[{ mese, episodes }]`; episodes with no date come last, under a
 * `mese` of null.
 */
export function episodesByMonth(index, programma = null) {
  const groups = [];
  for (const episode of latestEpisodes(index, Number.POSITIVE_INFINITY)) {
    if (programma && episode.programma !== programma) continue;
    const mese = monthOf(episode.data);
    const last = groups.at(-1);
    if (last && last.mese === mese) last.episodes.push(episode);
    else groups.push({ mese, episodes: [episode] });
  }
  return groups;
}

/** The address of the "Puntate" screen, for all programmes or for one. */
export function episodesPath(programma = null) {
  return programma ? `/puntate/${programma}` : '/puntate';
}
